import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, Popconfirm, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Doctors = () => {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [remainingSlots, setRemainingSlots] = useState(0);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        setLoading(true);
        try {
            const response = await api.get('/companyseller/doctors');
            setDoctors(response.data.data);
            setRemainingSlots(response.data.remainingSlots);
        } catch (error) {
            message.error('Failed to load doctors');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddDoctor = () => {
        if (remainingSlots === 0) {
            message.warning('You have reached your doctor limit. Please contact admin to increase your limit.');
            return;
        }
        form.resetFields();
        setEditMode(false);
        setModalVisible(true);
    };

    const handleEditDoctor = (doctor) => {
        setSelectedDoctor(doctor);
        setEditMode(true);
        form.setFieldsValue(doctor);
        setModalVisible(true);
    };

    const handleDeleteDoctor = async (id) => {
        try {
            await api.delete(`/companyseller/doctor/${id}`);
            message.success('Doctor deactivated successfully');
            fetchDoctors();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to deactivate doctor');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editMode) {
                await api.put(`/companyseller/doctor/${selectedDoctor._id}`, values);
                message.success('Doctor updated successfully');
            } else {
                await api.post('/companyseller/create-doctor', values);
                message.success('Doctor created successfully');
            }
            setModalVisible(false);
            fetchDoctors();
        } catch (error) {
            message.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const viewDoctorDetails = (doctorId) => {
        navigate(`/companyseller/doctor/${doctorId}`);
    };

    const columns = [
        {
            title: t('name') || 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('phoneNumber') || 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: t('email') || 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: t('specialization') || 'Specialization',
            dataIndex: 'specialization',
            key: 'specialization',
        },
        {
            title: t('patients') || 'Patients',
            dataIndex: 'patientCount',
            key: 'patientCount',
            render: (count) => <Tag color="blue">{count || 0}</Tag>
        },
        {
            title: t('visits') || 'Visits',
            dataIndex: 'visitCount',
            key: 'visitCount',
            render: (count) => <Tag color="green">{count || 0}</Tag>
        },
        {
            title: t('status') || 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
                </Tag>
            ),
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => viewDoctorDetails(record._id)}
                    >
                        {t('view') || 'View'}
                    </Button>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEditDoctor(record)}
                    >
                        {t('edit') || 'Edit'}
                    </Button>
                    <Popconfirm
                        title={t('confirmDeactivate') || 'Are you sure you want to deactivate this doctor?'}
                        onConfirm={() => handleDeleteDoctor(record._id)}
                        okText={t('yes') || 'Yes'}
                        cancelText={t('no') || 'No'}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            {t('deactivate') || 'Deactivate'}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2>{t('doctors') || 'Doctors'}</h2>
                    <p style={{ color: '#666', margin: 0 }}>
                        {t('doctorsCount') || 'Total Doctors'}: {doctors.length} |
                        {' '}{t('remainingSlots') || 'Remaining Slots'}: {remainingSlots === 'unlimited' ? '∞' : remainingSlots}
                    </p>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddDoctor}
                    disabled={remainingSlots === 0}
                >
                    {t('addDoctor') || 'Add Doctor'}
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={doctors}
                rowKey="_id"
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editMode ? (t('editDoctor') || 'Edit Doctor') : (t('addDoctor') || 'Add Doctor')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="name"
                        label={t('name') || 'Name'}
                        rules={[{ required: true, message: t('pleaseEnterName') || 'Please enter name' }]}
                    >
                        <Input placeholder={t('enterName') || 'Enter doctor name'} />
                    </Form.Item>

                    <Form.Item
                        name="phoneNumber"
                        label={t('phoneNumber') || 'Phone Number'}
                        rules={[{ required: true, message: t('pleaseEnterPhone') || 'Please enter phone number' }]}
                    >
                        <Input placeholder={t('enterPhone') || 'Enter phone number'} disabled={editMode} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t('email') || 'Email'}
                    >
                        <Input type="email" placeholder={t('enterEmail') || 'Enter email'} />
                    </Form.Item>

                    {!editMode && (
                        <Form.Item
                            name="password"
                            label={t('password') || 'Password'}
                            rules={[{ required: true, message: t('pleaseEnterPassword') || 'Please enter password' }]}
                        >
                            <Input.Password placeholder={t('enterPassword') || 'Enter password'} />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="specialization"
                        label={t('specialization') || 'Specialization'}
                    >
                        <Input placeholder={t('enterSpecialization') || 'Enter specialization'} />
                    </Form.Item>

                    <Form.Item
                        name="address"
                        label={t('address') || 'Address'}
                    >
                        <Input.TextArea rows={2} placeholder={t('enterAddress') || 'Enter address'} />
                    </Form.Item>

                    <Form.Item
                        name="clinicAddress"
                        label={t('clinicAddress') || 'Clinic Address'}
                    >
                        <Input.TextArea rows={2} placeholder={t('enterClinicAddress') || 'Enter clinic address'} />
                    </Form.Item>

                    <Form.Item
                        name="clinicPhone"
                        label={t('clinicPhone') || 'Clinic Phone'}
                    >
                        <Input placeholder={t('enterClinicPhone') || 'Enter clinic phone'} />
                    </Form.Item>

                    <Form.Item
                        name="language"
                        label={t('language') || 'Language'}
                        initialValue="en"
                    >
                        <Select>
                            <Select.Option value="en">{t('english') || 'English'}</Select.Option>
                            <Select.Option value="ar">{t('arabic') || 'Arabic'}</Select.Option>
                            <Select.Option value="ku">{t('kurdish') || 'Kurdish'}</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Doctors;
